import { Dialog, DialogContent, DialogTitle } from '@/components/Dialog';
import { cn } from '@/lib/cn';
import { DialogDescription } from '@radix-ui/react-dialog';
import { DataStreamBaseTileConfig } from '@squaredup/data-streams';
import type { Serialised } from '@squaredup/ids';
import type { PluginImportConfig } from '@squaredup/plugins';
import Button from 'components/button/Button';
import { ConfirmationPrompt } from 'components/ConfirmationPrompt';
import { ResizablePanelResizeHandle } from 'components/ResizablePanelResizeHandle';
import Tooltip from 'components/tooltip/Tooltip';
import { ClientDataStreamsContextProvider } from 'dashboard-engine/dataStreams/clientDataStreams/ClientDataStreamsContext';
import type { PluginConfigEntity } from 'dynamo-wrapper';
import { datasourceConfigQueryKeys } from 'queries/queryKeys/datasourceConfigKeys';
import { datasourceQueryKeys } from 'queries/queryKeys/datasourceKeys';
import { ComponentRef, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { Update } from 'services/SourceConfigService';
import DatasetContext from 'ui/editor/dataStream/contexts/DatasetContext';
import TileEditorContext, { useTileEditorContext } from 'ui/editor/dataStream/contexts/TileEditorContext';
import { EditorSteps } from 'ui/editor/dataStream/TileEditor/constants';
import { useTileEditorStepsContext } from 'ui/editor/dataStream/TileEditor/contexts/TileEditorStepsContext';
import { useDatasets } from 'ui/editor/dataStream/TileEditor/hooks/useDatasets';
import { useDataStreamType } from 'ui/editor/dataStream/TileEditor/hooks/useDataStreamType';
import { TileEditorStoreProviderFromDataSetContext } from 'ui/editor/dataStream/TileEditor/state/TileEditorStoreProvider';
import { DataStreamTileEditorSteps } from 'ui/editor/dataStream/TileEditor/steps/DataStreamTileEditorSteps';
import {
    PluginImportConfigEditorContextWrapper,
    usePluginImportConfigEditorContext
} from './PluginImportConfigEditorContext';
import { PluginImportConfigEditorDataPanel } from './PluginImportConfigEditorDataPanel';
import { PluginImportConfigStepsContextWrapper } from './PluginImportConfigStepsContextWrapper';

export const editorStepsHeightPercentage = 60;
const defaultSecondaryViewingPanelSize = 246;

export const PluginImportConfigEditorPrimaryViewingPanel = () => {
    const { editorSteps, currentEditorStep } = useTileEditorStepsContext();
    const editorStep = editorSteps.find(({ name }) => name === currentEditorStep);

    return (
        <div
            className={cn(
                'h-full min-h-0 relative isolate bg-tileBackground',
                editorStep && 'border flex flex-col bg-tileBackground border-dividerTertiary'
            )}
        >
            {editorStep && <editorStep.Content {...editorStep.contentProps} />}
        </div>
    );
};

export const PluginImportConfigEditorWrapper: React.FC<{ pluginId: string }> = ({ pluginId }) => {
    const datasetState = useDatasets();

    const verticalPanelGroupRef = useRef<ComponentRef<typeof PanelGroup>>(null);

    const handleResetVerticalPanelGroupLayout = () => {
        verticalPanelGroupRef.current?.setLayout([
            { sizePercentage: editorStepsHeightPercentage },
            { sizePercentage: 100 - editorStepsHeightPercentage }
        ]);
    };

    return (
        <div className='flex h-full p-4 overflow-hidden'>
            <DatasetContext.Provider value={datasetState}>
                <TileEditorStoreProviderFromDataSetContext>
                    <PluginImportConfigStepsContextWrapper initialStep={EditorSteps.dataStream} dataSourceId={pluginId}>
                        <DataStreamTileEditorSteps unDockOnEscape={false} />

                        <div className='flex-1 min-w-0 ml-4'>
                            <PanelGroup ref={verticalPanelGroupRef} direction='vertical'>
                                <Panel collapsible={true} minSizePixels={100}>
                                    <PluginImportConfigEditorPrimaryViewingPanel />
                                </Panel>

                                <ResizablePanelResizeHandle
                                    direction='vertical'
                                    onResetLayout={handleResetVerticalPanelGroupLayout}
                                />

                                <Panel
                                    collapsible={true}
                                    minSizePixels={100}
                                    defaultSizePixels={defaultSecondaryViewingPanelSize}
                                >
                                    <PluginImportConfigEditorDataPanel />
                                </Panel>
                            </PanelGroup>
                        </div>
                    </PluginImportConfigStepsContextWrapper>
                </TileEditorStoreProviderFromDataSetContext>
            </DatasetContext.Provider>
        </div>
    );
};

type PluginConfig = Serialised<PluginConfigEntity> & {
    config: { importConfig: PluginImportConfig };
};

interface PluginImportConfigEditorProps {
    pluginConfig: Serialised<PluginConfigEntity>;
    onClose: () => void;
}

export const PluginImportConfigEditor: React.FC<PluginImportConfigEditorProps> = ({ onClose, pluginConfig }) => {
    const pluginId = pluginConfig.plugin?.pluginId!;
    const importConfig = (pluginConfig as PluginConfig)?.config?.importConfig ?? {};
    const originalTileConfig = importConfig.tileConfig ?? {};

    const [tileConfig, setTileConfig] = useState(originalTileConfig);
    const [containsError, setContainsError] = useState(false);

    const { dataStreamType, isLoading } = useDataStreamType(originalTileConfig);

    if (isLoading) {
        return <></>;
    }

    return (
        <Dialog open={true}>
            <DialogTitle>Configure Import</DialogTitle>
            <DialogDescription>Allows configuration of the custom import used by this data source.</DialogDescription>
            <DialogContent className='fixed inset-0 flex flex-col w-screen h-full max-h-full outline-none pointer-events-auto bg-tileEditorBackground animate-enter text-textPrimary'>
                <PluginImportConfigEditorContextWrapper config={importConfig} configId={pluginConfig.id}>
                    <TileEditorContext.Provider
                        value={{
                            inEditor: true,
                            tileConfig,
                            isDatasetMode: false,
                            savedTileConfig: originalTileConfig,
                            savedTileDataStreamType: dataStreamType,
                            containsError,
                            setTileConfig,
                            setContainsError,
                            onClose
                        }}
                    >
                        <ClientDataStreamsContextProvider isClientDataStreamsEnabled={true}>
                            <PluginImportConfigEditorHeader pluginConfig={pluginConfig as PluginConfig} />
                            <PluginImportConfigEditorWrapper pluginId={pluginId} />
                        </ClientDataStreamsContextProvider>
                    </TileEditorContext.Provider>
                </PluginImportConfigEditorContextWrapper>
            </DialogContent>
        </Dialog>
    );
};

export const PluginImportConfigEditorHeader: React.FC<{ pluginConfig: PluginConfig }> = ({ pluginConfig }) => {
    const queryClient = useQueryClient();

    const { tileConfig, containsError, onClose } = useTileEditorContext();
    const { setConfig, ...importConfig } = usePluginImportConfigEditorContext();
    const [confirmDiscard, setConfirmDiscard] = useState(false);

    const importConfigIsValid =
        Boolean(importConfig.sourceNameColumn) &&
        Boolean(importConfig.sourceIdColumn) &&
        (importConfig.sourceType?.mode === 'fixed'
            ? Boolean(importConfig.sourceType.value)
            : Boolean(importConfig.sourceType?.column));

    const { mutate: submit, isLoading: isSubmitting } = useMutation(
        async (data: PluginImportConfig & { tileConfig: DataStreamBaseTileConfig }) => {
            const dataConfig = {
                ...pluginConfig.config,
                importConfig: data
            };

            const configId = pluginConfig?.id;
            await Update(configId, pluginConfig.displayName, dataConfig);
        },
        {
            onSuccess() {
                onClose();
                queryClient.invalidateQueries(datasourceQueryKeys.detail(pluginConfig.plugin?.pluginId));
                queryClient.invalidateQueries(datasourceConfigQueryKeys.list);
                queryClient.invalidateQueries(datasourceConfigQueryKeys.detail(pluginConfig.id));
            }
        }
    );

    const onSave = async () => {
        submit({
            ...importConfig,
            tileConfig
        });
    };

    return (
        <>
            <header
                className='flex items-center pl-[38px] pr-[31px] py-[16px] gap-x-xxl bg-componentBackgroundSecondary text-textPrimary border-b border-dividerTertiary'
                tabIndex={0}
                data-theme='dark'
            >
                <div className='w-full'></div>
                <div className='flex items-center justify-end space-x-4'>
                    <Button variant='secondary' onClick={onClose} data-testid='tileEditorClose'>
                        Close
                    </Button>
                    <Tooltip
                        title='Complete all required fields on the Mapping step before saving.'
                        disabled={importConfigIsValid}
                    >
                        <Button
                            variant='primary'
                            disabled={containsError || isSubmitting || !importConfigIsValid}
                            onClick={() => onSave()}
                            data-testid='tileEditorSave'
                        >
                            Save
                        </Button>
                    </Tooltip>
                </div>
            </header>

            {confirmDiscard && (
                <ConfirmationPrompt
                    title='Discard Changes'
                    prompt="Any changes you've made will be lost"
                    onConfirm={() => onClose()}
                    onClose={() => setConfirmDiscard(false)}
                    confirmButtonText='Discard'
                    confirmButtonVariant='destructive'
                />
            )}
        </>
    );
};
